<script setup>
import { computed, ref, watch, onMounted, watchEffect } from 'vue';
import Chart from 'primevue/chart';
import { useUserStore } from '@/store/user-store';
import { chartColors, donutChartOptions } from '@/services/constant-services';
import { convertToInr } from '@/services/common-services';

const props = defineProps({
  selectedStock: { type: String, default: '' },
  currentPrice: { type: Number, default: 0 },
  fallbackSector: { type: String, default: '' },
  fallbackCapCategory: { type: String, default: null },
});

const userStore = useUserStore();

const plannedShares = ref(10);
const loading = ref(false);
const error = ref('');

const attributionData = ref(null);

// unwrapped core attribution object
const coreAttribution = computed(() => {
  const raw = attributionData.value;
  // expected shape: { responseData: { attributionData: { ... } } }
  if (raw?.responseData?.attributionData) return raw.responseData.attributionData;
  // fallback: maybe already the inner object
  if (raw?.sector_attribution || raw?.portfolio_value) return raw;
  return null;
});

// convenience flag: true only when coreAttribution is present
const hasAttribution = computed(() => !!coreAttribution.value);

const dashboardData = computed(() => userStore.dashboardData || {});

const portfolioId = computed(() => dashboardData.value?.portfolioId || null);

const loadAttribution = async () => {
  if (!portfolioId.value) return;
  try {
    loading.value = true;
    error.value = '';
    const resp = await userStore.getPortfolioAttributionData(portfolioId.value);
    attributionData.value = resp;
  } catch (e) {
    console.error(e);
    error.value = 'Could not load portfolio attribution.';
  } finally {
    loading.value = false;
  }
};

const refreshAttribution = async () => {
  console.log('Manually refreshing portfolio attribution data...');

  // Clear cached attribution data from user store to force fresh API call
  userStore.portfolioAttributionData = null;

  // Also clear any sessionStorage cache
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem('portfolioAttributionData');
  }

  console.log('Cleared attribution cache, making fresh API call...');
  await loadAttribution();
};

onMounted(() => {
  loadAttribution();
});

watch(() => portfolioId.value, () => {
  if (!userStore.portfolioAttributionData) {
    loadAttribution();
  } else {
    attributionData.value = userStore.portfolioAttributionData;
  }
});

const additionalInvestment = computed(() => {
  if (!props.selectedStock || !props.currentPrice || !plannedShares.value) return 0;
  return props.currentPrice * plannedShares.value;
});

// Helpers to find sector and cap category of selected stock from dashboardData,
// falling back to props.fallbackSector / fallbackCapCategory when not found.
const selectedStockInfo = computed(() => {
  const all = dashboardData.value?.stocksOwned || [];
  return all.find((s) => s.stockSymbol === props.selectedStock) || null;
});

const selectedSector = computed(() => {
  if (selectedStockInfo.value?.stockSector) return selectedStockInfo.value.stockSector;
  if (props.fallbackSector) return props.fallbackSector;
  return 'Others';
});

// Sector mapping: Maps frontend sector names to backend portfolio attribution sector names
const SECTOR_MAPPING = {
  'Healthcare': 'Pharmaceuticals',  // Healthcare stocks go to Pharmaceuticals in portfolio
  'Pharmaceuticals': 'Pharmaceuticals',
  'IT Services': 'IT Consulting & Software',
  'Autos / Auto Ancillaries': 'Automobiles',
  'Banks': 'Banks',
  'NBFC': 'NBFC',
  'Retail': 'Retail',
  'Construction': 'Construction & Engineering',
  'FMCG': 'FMCG',
  'Defense': 'Defense & Aerospace',
  'Insurance': 'Insurance',
  'Metals': 'Metals & Mining',
  'Telecom': 'Telecommunication',
  'Oil & Gas': 'Oil, Gas & Energy',
};

// Helper to map frontend sector to backend sector name
const mapSectorName = (frontendSector) => {
  if (!frontendSector) return 'Others';
  return SECTOR_MAPPING[frontendSector] || frontendSector || 'Others';
};

// Helper to normalize sector names for matching (handles variations like "Healthcare" vs "Health Care")
const normalizeSectorName = (name) => {
  if (!name) return '';
  return name.toLowerCase().replace(/\s+/g, '').trim();
};

// Find matching sector from attribution data, handling name variations
const matchingSector = computed(() => {
  const core = coreAttribution.value;
  if (!core) return null;
  const sectors = core.sector_attribution || [];
  if (!sectors.length) return null;

  const targetNormalized = normalizeSectorName(selectedSector.value);

  // Try exact match first
  let match = sectors.find((s) => s.sector === selectedSector.value);

  // Try normalized match if exact fails
  if (!match) {
    match = sectors.find((s) => normalizeSectorName(s.sector) === targetNormalized);
  }

  // If still no match, log available sectors for debugging
  if (!match) {
    console.warn('Selected sector not found in attribution:', {
      selectedSector: selectedSector.value,
      availableSectors: sectors.map(s => s.sector)
    });
  }

  return match;
});

const selectedCapCategory = computed(() => {
  if (selectedStockInfo.value?.stockCapCategory) return selectedStockInfo.value.stockCapCategory;
  if (props.fallbackCapCategory) return props.fallbackCapCategory;
  // Default to Mid Cap if no category is specified
  return 'Mid Cap';
});

watchEffect(() => {
  if (coreAttribution.value) {
    console.log('attributionData loaded in impact component', coreAttribution.value);
    console.log('sector_attribution length', coreAttribution.value.sector_attribution?.length || 0);
  }
});

// --- Sector impact donut data ---
const sectorDonutData = computed(() => {
  const core = coreAttribution.value;
  if (!core) {
    console.log('sectorDonutData: no coreAttribution yet');
    return { labels: [], datasets: [] };
  }
  const sectors = core.sector_attribution || [];
  if (!sectors.length) {
    console.log('sectorDonutData: sector_attribution empty');
    return { labels: [], datasets: [] };
  }

  const additional = additionalInvestment.value;
  const labels = sectors.map((s) => s.sector);
  const start = core.portfolio_value?.start_value || 0;

  console.log('=== SECTOR MATCHING DEBUG ===');
  console.log('Selected stock:', props.selectedStock);
  console.log('Selected sector from component:', selectedSector.value);
  console.log('Mapped backend sector:', mapSectorName(selectedSector.value));
  console.log('Selected sector normalized:', normalizeSectorName(mapSectorName(selectedSector.value)));
  console.log('Additional investment:', additional);
  console.log('Available sectors in portfolio:', labels);
  console.log('Available sectors normalized:', labels.map(s => normalizeSectorName(s)));

  const currentValues = sectors.map((s) => (s.portfolio_weight / 100) * start);
  console.log('sectorDonutData currentValues', currentValues);

  const mappedSectorName = mapSectorName(selectedSector.value);

  const projectedValues = sectors.map((s) => {
    const baseVal = (s.portfolio_weight / 100) * start;
    const sectorNorm = normalizeSectorName(s.sector);
    const selectedNorm = normalizeSectorName(mappedSectorName);
    const isMatch = sectorNorm === selectedNorm;

    console.log(`Checking sector "${s.sector}" (${sectorNorm}) vs mapped "${mappedSectorName}" (${selectedNorm}): ${isMatch ? 'MATCH' : 'no match'}`);

    // Use normalized comparison to handle sector name variations
    if (isMatch) {
      const proj = baseVal + additional;
      console.log('*** MATCH FOUND! Adding', additional, 'to base', baseVal, '= projected', proj);
      return proj;
    }
    return baseVal;
  });
  console.log('sectorDonutData projectedValues', projectedValues);

  const totalProjected = projectedValues.reduce((a, b) => a + b, 0) || 1;
  const projectedPct = projectedValues.map((v) => (v / totalProjected) * 100);

  return {
    labels,
    datasets: [
      {
        data: projectedPct.map((x) => Number(x.toFixed(2))),
        backgroundColor: chartColors.slice(0, labels.length),
      },
    ],
    projectedPct,
  };
});

// --- Market cap impact (Large/Mid/Small) ---
const capBuckets = ['Large Cap', 'Mid Cap', 'Small Cap'];

const currentCapValues = computed(() => {
  const dd = dashboardData.value;
  const stocks = dd?.stocksOwned || [];
  const fixedIncome = dd?.fixedIncome || [];
  const mutualFunds = dd?.mutualFunds || [];
  const otherInvest = dd?.otherInvest || [];

  const grouped = { 'Large Cap': 0, 'Mid Cap': 0, 'Small Cap': 0 };

  // Stocks by cap category
  stocks.forEach((s) => {
    const cap = s.stockCapCategory || 'Mid Cap';
    const val = Number(s.currentPrice || 0) * Number(s.numberOfShares || 0);
    if (!grouped[cap]) grouped[cap] = 0;
    grouped[cap] += val;
  });

  // Everything else is not mapped to caps but still in total portfolio value
  let totalOther = 0;

  mutualFunds.forEach((mf) => {
    totalOther += Number(mf.currentNAV || 0) * Number(mf.numberOfUnits || 0);
  });
  fixedIncome.forEach((fi) => {
    totalOther += Number(fi.amountInvested || 0);
  });
  otherInvest.forEach((oi) => {
    totalOther += Number(oi.amountInvested || 0);
  });

  const stockCapTotal = capBuckets.reduce((sum, cap) => sum + (grouped[cap] || 0), 0);
  const grandTotal = stockCapTotal + totalOther;

  return { grouped, grandTotal };
});

const capDonutData = computed(() => {
  const { grouped, grandTotal } = currentCapValues.value;
  const labels = capBuckets;
  if (!grandTotal) return { labels: [], datasets: [], projectedPct: [] };

  const currentValues = labels.map((cap) => grouped[cap] || 0);
  const additional = additionalInvestment.value;

  const baseTotal = grandTotal;

  const projectedValues = labels.map((cap) => {
    if (cap === selectedCapCategory.value) return (grouped[cap] || 0) + additional;
    return (grouped[cap] || 0);
  });

  const projectedTotal = (baseTotal + (selectedCapCategory.value ? additional : 0)) || 1;

  const projectedPct = projectedValues.map((v) => (projectedTotal ? (v / projectedTotal) * 100 : 0));

  return {
    labels,
    datasets: [
      {
        data: projectedPct.map((x) => Number(x.toFixed(2))),
        backgroundColor: chartColors.slice(0, labels.length),
      },
    ],
    projectedPct,
  };
});

// Target asset allocation helper
const targetAllocation = computed(() => coreAttribution.value?.TargetAssetAllocation || []);

const findTargetForCap = (capName) => {
  const row = targetAllocation.value.find((r) => r.AssetClassName === capName);
  return row ? row.TargetPct : null;
};

const capRiskFlags = computed(() => {
  const { grouped, grandTotal } = currentCapValues.value;
  const labels = capBuckets;
  const additional = additionalInvestment.value;

  const baseTotal = grandTotal || 1;
  const projectedValues = labels.map((cap) => {
    if (cap === selectedCapCategory.value) return (grouped[cap] || 0) + additional;
    return grouped[cap] || 0;
  });
  const projectedTotal = baseTotal + (selectedCapCategory.value ? additional : 0);

  const flags = {};
  labels.forEach((cap, idx) => {
    const projPct = projectedTotal ? (projectedValues[idx] / projectedTotal) * 100 : 0;
    const target = findTargetForCap(cap);
    if (target === null) {
      flags[cap] = { projected: projPct, target: null, breach: true };
    } else {
      flags[cap] = { projected: projPct, target, breach: projPct > target };
    }
  });
  return flags;
});

const sectorRiskFlags = computed(() => {
  const core = coreAttribution.value;
  if (!core) return {};
  const sectors = core.sector_attribution || [];
  if (!sectors.length) return {};
  const additional = additionalInvestment.value;
  const start = core.portfolio_value?.start_value || 0;

  const mappedSectorName = mapSectorName(selectedSector.value);

  const projectedValues = sectors.map((s) => {
    const baseVal = (s.portfolio_weight / 100) * start;
    // Use normalized comparison with mapped sector name
    if (normalizeSectorName(s.sector) === normalizeSectorName(mappedSectorName)) {
      return baseVal + additional;
    }
    return baseVal;
  });
  const totalProjected = projectedValues.reduce((a, b) => a + b, 0) || 1;

  const flags = {};
  sectors.forEach((s, idx) => {
    const projPct = (projectedValues[idx] / totalProjected) * 100;
    flags[s.sector] = { projected: projPct, breach: projPct > 20 };
  });
  return flags;
});

const nonZeroSectorLabels = computed(() => {
  const core = coreAttribution.value;
  if (!core) return [];
  const sectors = core.sector_attribution || [];
  return sectors
    .filter((s) => (sectorRiskFlags.value[s.sector]?.projected || 0) > 0)
    .map((s) => s.sector);
});

// Color helpers
const sectorColorFor = (sector) => {
  const idx = sectorDonutData.value.labels.indexOf(sector);
  if (idx === -1) return chartColors[0];
  return chartColors[idx % chartColors.length];
};

const capColorFor = (cap) => {
  const idx = capBuckets.indexOf(cap);
  if (idx === -1) return chartColors[0];
  return chartColors[idx % chartColors.length];
};

const sectorCurrentPct = (sectorName) => {
  const core = coreAttribution.value;
  if (!core) return 0;
  const sectors = core.sector_attribution || [];
  const start = core.portfolio_value?.start_value || 0;
  const s = sectors.find((x) => x.sector === sectorName);
  if (!s || !start) return 0;
  // current weight is portfolio_weight field itself (already %), keep 2 decimals in UI
  return Number(s.portfolio_weight || 0);
};

const abbreviateSector = (name) => {
  if (!name) return '';
  if (name.length <= 12) return name;
  return name.slice(0, 9) + '...';
};
</script>

<template>
  <div class="impact-root">
    <div class="impact-header-row">
      <h3 class="impact-title">Impact Analysis of Stock Purchase on Portfolio</h3>
      <div class="header-actions">
        <span v-if="selectedStock" class="impact-stock-pill">{{ selectedStock }}</span>
        <button
          v-if="hasAttribution || loading"
          class="refresh-button-small"
          @click="refreshAttribution"
          :disabled="loading"
          title="Refresh portfolio data"
        >
          <i class="pi pi-refresh" :class="{ 'pi-spin': loading }"></i>
        </button>
      </div>
    </div>

    <div v-if="error" class="impact-error">{{ error }}</div>
    <div v-else-if="loading" class="impact-loading">Loading portfolio attribution…</div>

    <div v-else-if="!selectedStock">
      <p class="impact-hint">Select a stock above to see its impact on your portfolio.</p>
    </div>

    <div v-else-if="!hasAttribution">
      <div class="no-data-state">
        <p class="impact-hint">Preparing portfolio data…</p>
        <button
          class="refresh-button"
          @click="refreshAttribution"
          :disabled="loading"
        >
          <i class="pi pi-refresh" :class="{ 'pi-spin': loading }"></i>
          {{ loading ? 'Loading...' : 'Refresh Data' }}
        </button>
      </div>
    </div>

    <div v-else class="impact-content">
      <div class="impact-controls">
        <div class="impact-input-row impact-input-row--stepper">
          <label class="impact-label" for="plannedShares">Planned Purchase</label>
          <input
            id="plannedShares"
            v-model.number="plannedShares"
            type="number"
            min="0"
            max="500"
            step="5"
            class="impact-stepper"
          />
          <span class="impact-stepper-suffix">No. of Shares</span>
        </div>
        <div class="impact-amount">
          Approx. investment: <strong>{{ convertToInr(additionalInvestment) }}</strong>
        </div>
      </div>

      <div class="impact-grids">
        <!-- Sector card -->
        <div class="impact-card">
          <h4 class="impact-card-title">Sector weights (current vs projected)</h4>
          <p v-if="!sectorDonutData.labels.length" class="impact-hint">No sector data available yet.</p>
          <Chart
            v-else
            type="doughnut"
            :data="sectorDonutData"
            :options="donutChartOptions"
            class="impact-chart"
          />

          <!-- Compact sector legend table -->
          <table
            v-if="nonZeroSectorLabels.length"
            class="impact-sector-legend-table"
          >
            <thead>
              <tr>
                <th class="legend-header" />
                <th class="value-header">Projected</th>
                <th class="value-header">Current</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="sector in nonZeroSectorLabels"
                :key="sector"
                :class="{ breach: sectorRiskFlags[sector]?.breach }"
              >
                <td class="legend-cell">
                  <span
                    class="dot"
                    :style="{ backgroundColor: sectorColorFor(sector) }"
                  />
                  <span
                    class="text"
                    :title="sector"
                  >
                    {{ abbreviateSector(sector) }}
                  </span>
                </td>
                <td
                  class="value-cell"
                  :class="{ breach: sectorRiskFlags[sector]?.breach }"
                  :title="sectorRiskFlags[sector]?.breach ? 'Projected weight exceeds 20% cap' : ''"
                >
                  {{ (sectorRiskFlags[sector]?.projected || 0).toFixed(2) }}%
                </td>
                <td
                  class="value-cell"
                  :class="{ breach: sectorRiskFlags[sector]?.breach }"
                  :title="sectorRiskFlags[sector]?.breach ? 'Current + projected weight exceeds 20% cap' : ''"
                >
                  {{ sectorCurrentPct(sector).toFixed(2) }}%
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Market-cap card -->
        <div class="impact-card">
          <h4 class="impact-card-title">Market-cap allocation (stocks only)</h4>
          <div class="impact-cap-layout">
            <div class="impact-cap-left">
              <Chart
                v-if="capDonutData.labels.length && capDonutData.datasets[0]?.data?.length"
                type="doughnut"
                :data="capDonutData"
                :options="donutChartOptions"
                class="impact-chart"
              />
              <table class="impact-cap-legend-table">
                <thead>
                  <tr>
                    <th class="legend-header" />
                    <th class="value-header">Projected</th>
                    <th class="value-header">Target</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="cap in capBuckets"
                    :key="cap"
                    :class="{ breach: capRiskFlags[cap]?.breach }"
                  >
                    <td class="legend-cell">
                      <span
                        class="dot dot--cap"
                        :style="{ backgroundColor: capColorFor(cap) }"
                      />
                      <span class="text">{{ cap }}</span>
                    </td>
                    <td class="value-cell">
                      {{ (capRiskFlags[cap]?.projected || 0).toFixed(2) }}%
                    </td>
                    <td class="value-cell">
                      {{ capRiskFlags[cap] ? (capRiskFlags[cap].target ?? '—') + '%' : '—' }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="impact-cap-right">
              <table class="impact-target-table impact-target-table--center">
                <thead>
                  <tr>
                    <th>Asset class</th>
                    <th>Target %</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in targetAllocation" :key="row.AssetClassName">
                    <td>{{ row.AssetClassName }}</td>
                    <td>{{ row.TargetPct }}%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.impact-root {
  margin-top: 16px;
  padding: 16px;
  border-radius: 10px;
  border: 1px solid #e9ecef;
  background: #fff;
}

.impact-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 4px;
}

@media (max-width: 768px) {
  .impact-header-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}

@media (max-width: 480px) {
  .impact-header-row {
    text-align: center;
    align-items: center;
  }
}

.impact-title {
  margin: 0;
  font-size: 0.96rem;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.refresh-button-small {
  background: var(--an-primary, #3b82f6);
  border: none;
  border-radius: 6px;
  padding: 6px 8px;
  color: white;
  cursor: pointer;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  min-width: 32px;
  height: 28px;
}

.refresh-button-small:hover:not(:disabled) {
  background: var(--an-primary-dark, #2563eb);
  transform: translateY(-1px);
}

.refresh-button-small:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  transform: none;
}

.refresh-button {
  background: var(--an-primary, #3b82f6);
  border: none;
  border-radius: 8px;
  padding: 12px 20px;
  color: white;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  margin-top: 16px;
}

.refresh-button:hover:not(:disabled) {
  background: var(--an-primary-dark, #2563eb);
  transform: translateY(-1px);
}

.refresh-button:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  transform: none;
}

.no-data-state {
  text-align: center;
  padding: 20px;
}

.impact-stock-pill {
  padding: 4px 10px;
  border-radius: 999px;
  background: #e3f7fb; /* light blue like Fair Value Dial */
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.03em;
}

.impact-error {
  color: #c92a2a;
  font-size: 0.85rem;
}

.impact-loading,
.impact-hint {
  font-size: 0.85rem;
  color: #495057;
}

.impact-controls {
  margin-bottom: 12px;
}

/* Responsive controls */
@media (max-width: 480px) {
  .impact-input-row--stepper {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .impact-stepper {
    width: 120px;
  }
}

.impact-input-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.impact-input-row--stepper {
  justify-content: flex-start;
  gap: 12px;
}

.impact-label {
  font-size: 0.82rem;
  font-weight: 600;
  white-space: nowrap;
}

.impact-stepper {
  width: 80px;
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid #adb5bd;
  font-size: 0.85rem;
}

.impact-stepper-suffix {
  font-size: 0.85rem;
  white-space: nowrap;
}

.impact-amount {
  margin-top: 4px;
  font-size: 0.8rem;
  color: #495057;
}

.impact-grids {
  display: grid;
  grid-template-columns: 0.9fr 1.1fr; /* shrink sector card ~10%, expand market-cap card ~10% */
  gap: 16px;
}

/* Responsive layout for impact grids */
@media (max-width: 1200px) {
  .impact-grids {
    grid-template-columns: 1fr 1fr; /* Equal columns on medium screens */
    gap: 12px;
  }
}

@media (max-width: 768px) {
  .impact-grids {
    grid-template-columns: 1fr; /* Stack vertically on mobile */
    gap: 16px;
  }

  .impact-card {
    padding: 16px;
  }

  .impact-card-title {
    font-size: 1rem;
    margin-bottom: 12px;
  }
}

@media (max-width: 480px) {
  .impact-grids {
    gap: 12px;
  }

  .impact-card {
    padding: 12px;
  }

  .impact-card-title {
    font-size: 0.95rem;
    text-align: center;
  }
}

.impact-card {
  border-radius: 10px;
  border: 1px solid #e9ecef;
  padding: 12px 14px;
}

.impact-card-title {
  margin: 0 0 8px;
  font-size: 0.9rem;
}

.impact-chart {
  max-width: 260px;
  margin: 0 auto 8px;
}

/* Responsive Chart Sizing */
@media (max-width: 1024px) {
  .impact-chart {
    max-width: 240px;
  }
}

@media (max-width: 768px) {
  .impact-chart {
    max-width: 200px;
  }
}

@media (max-width: 480px) {
  .impact-chart {
    max-width: 180px;
  }
}

.impact-legend {
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: 0.8rem;
}

.impact-legend li {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
}

.impact-legend li.breach .text {
  color: #c92a2a;
}

.impact-cap-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr); /* donut+legend left, target table right */
  gap: 8px;
  align-items: flex-start;
}

/* Responsive Market-cap Card Layout */
@media (max-width: 1024px) {
  .impact-cap-layout {
    grid-template-columns: 1fr; /* Stack chart above table */
    gap: 12px;
    text-align: center;
  }

  .impact-cap-left {
    align-items: center;
  }

  .impact-cap-right {
    justify-content: center;
  }

  .impact-target-table {
    max-width: 300px;
    margin: 0 auto;
  }
}

@media (max-width: 768px) {
  .impact-cap-layout {
    gap: 16px;
  }

  .impact-chart {
    max-width: 200px; /* Smaller chart on mobile */
  }
}

@media (max-width: 480px) {
  .impact-cap-layout {
    gap: 12px;
  }

  .impact-chart {
    max-width: 180px;
  }

  .impact-target-table {
    font-size: 0.75rem;
  }
}

.impact-cap-left {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.impact-cap-right {
  display: flex;
  justify-content: flex-start;
}

.impact-cap-legend-table {
  margin-top: 6px;
  font-size: 0.8rem;
  border-collapse: collapse;
  width: auto; /* keep compact */
}

.impact-cap-legend-table th,
.impact-cap-legend-table td {
  border: none;
  padding: 2px 6px;
}

/* Responsive Market Cap Legend Table */
@media (max-width: 768px) {
  .impact-cap-legend-table {
    width: 100%;
    font-size: 0.75rem;
  }

  .impact-cap-legend-table th,
  .impact-cap-legend-table td {
    padding: 4px 8px;
  }
}

@media (max-width: 480px) {
  .impact-cap-legend-table {
    font-size: 0.7rem;
  }

  .impact-cap-legend-table th,
  .impact-cap-legend-table td {
    padding: 3px 6px;
  }
}

.legend-header {
  text-align: left;
  white-space: nowrap;
}

.value-header {
  text-align: right;
  white-space: nowrap;
}

.legend-cell {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.value-cell {
  text-align: right;
  white-space: nowrap;
}

/* keep breach coloring */
.impact-cap-legend-table tr.breach .value-cell {
  color: #c92a2a;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #868e96;
  display: inline-block;
}

.dot--cap {
  flex-shrink: 0;
}

/* Center-align target asset allocation table, except first column */
.impact-target-table--center th,
.impact-target-table--center td {
  text-align: center;
}

.impact-target-table--center th:first-child,
.impact-target-table--center td:first-child {
  text-align: left;
}

.ml-4 {
  margin-left: 4px;
}

.impact-target-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.78rem;
}

/* ...existing target-table cell styles remain... */

.impact-sector-legend-table {
  margin-top: 6px;
  font-size: 0.8rem;
  border-collapse: collapse;
  width: auto; /* compact */
}

.impact-sector-legend-table th,
.impact-sector-legend-table td {
  border: none;
  padding: 2px 6px;
}

.impact-sector-legend-table .legend-cell {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.impact-sector-legend-table .value-cell {
  text-align: right;
  white-space: nowrap;
}

.impact-sector-legend-table tr.breach .legend-cell .text,
.impact-sector-legend-table tr.breach .value-cell,
.impact-sector-legend-table .value-cell.breach {
  color: #c92a2a;
}

/* Responsive Sector Table */
@media (max-width: 768px) {
  .impact-sector-legend-table {
    width: 100%;
    font-size: 0.75rem;
  }

  .impact-sector-legend-table th,
  .impact-sector-legend-table td {
    padding: 4px 8px;
  }
}

@media (max-width: 480px) {
  .impact-sector-legend-table {
    font-size: 0.7rem;
  }

  .impact-sector-legend-table th,
  .impact-sector-legend-table td {
    padding: 3px 6px;
  }
}

/* ...rest of styles unchanged... */
</style>
